import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Calm in-flight chrome — plan/27 action feedback.
 * Spinner + -ing copy so slow links never feel stuck.
 */
export function PendingSpinner(props: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-block size-3.5 shrink-0 rounded-full border-2 border-current border-r-transparent animate-spin opacity-80",
        props.className,
      )}
      aria-hidden
    />
  );
}

/** Inline spinner + label (buttons, chips, short status). */
export function PendingInline(props: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn("inline-flex items-center gap-2", props.className)}
      role="status"
      aria-live="polite"
    >
      <PendingSpinner />
      <span>{props.children}</span>
    </span>
  );
}

/** Block status line for list/page loads. */
export function PendingStatus(props: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "m-0 flex items-center gap-2 text-sm text-[var(--color-muted)] animate-[fadeIn_160ms_ease-out]",
        props.className,
      )}
      role="status"
      aria-live="polite"
    >
      <PendingSpinner className="text-[var(--color-primary)]" />
      <span>{props.children}</span>
    </p>
  );
}

/** Thin indeterminate bar under a form / login — unknown duration. */
export function PendingBar(props: { className?: string; label?: string }) {
  return (
    <div
      className={cn("w-full", props.className)}
      role="status"
      aria-live="polite"
      aria-label={
        typeof props.label === "string" ? props.label : "Working…"
      }
    >
      {props.label ? (
        <PendingInline className="mb-2 text-sm text-[var(--color-muted)]">
          {props.label}
        </PendingInline>
      ) : null}
      <div className="h-0.5 w-full overflow-hidden rounded-full bg-[var(--color-border)]/70">
        <div className="h-full w-1/3 rounded-full bg-[var(--color-primary)] animate-[progressSlide_1.1s_ease-in-out_infinite]" />
      </div>
    </div>
  );
}
